import { useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useState } from "react";

import MessageInput from "@/components/chat/MessageInput";
import { PageLayout } from "@/components/layout";
import { toast } from "@/components/ui/Toast/useToast";
import { useStore } from "@/store";
import { useChatStore } from "@/store/chat/chat.store";
import type { TMessageFile } from "@/types/chat";
import { Loader2 } from "lucide-react";
import { useShallow } from "zustand/react/shallow";

const normalizeTitle = (value: string) => {
  const cleaned = value.trim().replace(/\s+/g, " ");
  if (!cleaned) {
    return "New Chat";
  }
  const words = cleaned.split(" ");
  const snippet = words.slice(0, 6).join(" ");
  return snippet.length > 60 ? `${snippet.slice(0, 57)}...` : snippet;
};

/**
 * Section new chat page component.
 * Renders a simple new chat UI that creates the thread on submit and navigates to the thread route.
 * NO WebSocket connection - just a draft UI until thread is created.
 */
export default function SectionNewChatPage() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const companyId = params.company_id;
  const sectionId = params.section_id;

  const fetchCompanies = useStore((state) => state.fetchCompanies);
  const companies = useStore((state) => state.companies);

  const {
    openChatThreadSession,
    setThreadTitle,
    createThread,
    isCreatingThread: storeIsCreating,
  } = useChatStore(
    useShallow((state) => ({
      openChatThreadSession: state.openChatThreadSession,
      setThreadTitle: state.setThreadTitle,
      createThread: state.createThread,
      isCreatingThread: state.isCreatingThread,
    }))
  );

  const [isCreatingThread, setIsCreatingThread] = useState(false);

  const isCreating = isCreatingThread || storeIsCreating;

  const company = companies?.find((c) => c.id === companyId);
  const section = company?.sections?.find((s) => s.id === sectionId);

  const handleStartNewChat = useCallback(
    async (content: string, files?: TMessageFile[]) => {
      if (!companyId || !sectionId) {
        toast({ variant: "destructive", title: "Cannot start chat", description: "Missing company or section information." });
        return;
      }

      const trimmed = content.trim();
      if (!trimmed) {
        return;
      }

      if (files && files.length > 0) {
        toast({ variant: "destructive", title: "Not yet supported", description: "File uploads are available after the chat starts." });
        return;
      }

      setIsCreatingThread(true);

      const chatTitle = normalizeTitle(trimmed);

      try {
        // Create thread via store action
        const result = await createThread({
          company_id: companyId,
          section_id: sectionId,
          title: chatTitle,
        });

        if (!result.success || !result.thread) {
          toast({ variant: "destructive", title: "Could not create chat", description: result.error ?? "Failed to create chat thread. Please try again." });
          setIsCreatingThread(false);
          return;
        }

        const thread = result.thread;
        setThreadTitle(thread.id, thread.title ?? chatTitle);

        // Open the chat session with the new thread and initial prompt
        openChatThreadSession({
          threadId: thread.id,
          companyId,
          companyName: company?.name,
          sectionId: thread.section_id ?? sectionId,
          sectionName: section?.name ?? "Section",
          title: thread.title ?? chatTitle,
          initialPrompt: trimmed,
        });

        // Refresh companies data to update sidebar with new thread
        void fetchCompanies({ force: true });

        // Navigate to the thread route
        void navigate({
          to: "/app/companies/$company_id/sections/$section_id/threads/$thread_id",
          params: {
            company_id: thread.company_id,
            section_id: thread.section_id ?? sectionId,
            thread_id: thread.id,
          },
        });
      } catch (creationError) {
        console.error("[new-chat] Failed to create thread", creationError);
        toast({ variant: "destructive", title: "Could not create chat", description: "Failed to create chat thread. Please try again." });
      } finally {
        setIsCreatingThread(false);
      }
    },
    [
      companyId,
      sectionId,
      company,
      section,
      createThread,
      navigate,
      openChatThreadSession,
      setThreadTitle,
      fetchCompanies,
    ]
  );

  return (
    <PageLayout>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex flex-1 items-center justify-center overflow-y-auto bg-muted">
          {isCreating ? (
            <div className="flex flex-col items-center gap-3 px-4 text-center text-muted-foreground">
              <Loader2
                className="h-8 w-8 animate-spin text-primary"
                aria-hidden="true"
              />
              <p className="text-sm font-medium">Creating your chat...</p>
            </div>
          ) : (
            <div className="px-4 text-center text-muted-foreground">
              <p className="text-sm">What would you like to explore?</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Your chat will be saved here as you go.
              </p>
            </div>
          )}
        </div>
        <MessageInput
          onSend={(message, files) => {
            void handleStartNewChat(message, files);
          }}
          disabled={isCreating}
        />
      </div>
    </PageLayout>
  );
}
